import React, { useState, useRef, useEffect } from "react";
import { Download, FileText, Table2, Loader2, ChevronDown } from "lucide-react";

interface ExportButtonProps {
  exporting:       boolean;
  onExportCSV:     () => void;
  onExportReport:  () => void;
  compareEnabled?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  exporting, onExportCSV, onExportReport, compareEnabled,
}) => {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => !exporting && setOpen(o => !o)}
        disabled={exporting}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
          border cursor-pointer select-none transition-all duration-150 tracking-tight
          disabled:opacity-50 disabled:cursor-not-allowed
          ${open
            ? "bg-slate-800 text-white border-slate-700"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800 hover:bg-slate-50"
          }
        `}
      >
        {exporting
          ? <Loader2 size={13} strokeWidth={2} className="animate-spin" />
          : <Download size={13} strokeWidth={2} />}
        Exporter
        <ChevronDown size={11} strokeWidth={2}
          className={`transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="
          absolute right-0 top-[calc(100%+6px)] z-50
          bg-white border border-slate-100 rounded-xl shadow-xl min-w-[224px]
          overflow-hidden animate-[compareSlideIn_0.15s_ease]
        ">
          <div className="px-3.5 py-2.5 border-b border-slate-50">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide m-0">
              Exporter les données
            </p>
            {compareEnabled && (
              <p className="text-[10px] text-violet-500 font-medium m-0 mt-0.5">Inclut les 2 périodes</p>
            )}
          </div>

           <button
            onClick={() => { setOpen(false); onExportCSV(); }}
            className="w-full flex items-start gap-3 px-3.5 py-3 text-left cursor-pointer border-none bg-transparent hover:bg-slate-50 transition-colors duration-100 group"
          >
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-emerald-100 transition-colors">
              <Table2 size={14} strokeWidth={2} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-800 m-0 leading-tight">Fichier CSV</p>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5 leading-snug">
                Compatible Excel · toutes métriques{compareEnabled ? " · 2 périodes" : ""}
              </p>
            </div>
          </button>

           <button
            onClick={() => { setOpen(false); onExportReport(); }}
            className="w-full flex items-start gap-3 px-3.5 py-3 text-left cursor-pointer border-none bg-transparent hover:bg-slate-50 transition-colors duration-100 group border-t border-slate-50"
          >
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-blue-100 transition-colors">
              <FileText size={14} strokeWidth={2} className="text-blue-600" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-slate-800 m-0 leading-tight">Rapport PDF</p>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5 leading-snug">
                Nouvel onglet · imprimable en PDF
              </p>
            </div>
          </button>

          <div className="px-3.5 py-2 bg-slate-50 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 m-0">
              Données de la période sélectionnée
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportButton;